import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft, Upload, Truck, DollarSign, Clock, AlertTriangle,
  FileText, User, Package, MapPin, ChevronRight, Check
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: "order_placed", label: "Order Placed" },
  { id: "proof_sent", label: "Proof Sent" },
  { id: "proof_approved", label: "Proof Approved" },
  { id: "in_production", label: "In Production" },
  { id: "quality_check", label: "Quality Check" },
  { id: "ready_to_ship", label: "Ready to Ship" },
  { id: "shipped", label: "Shipped" },
];

const PAYMENT_MAP = {
  paid: { cls: "bg-green-500/20 text-green-400 border-green-500/30", label: "Paid" },
  partial: { cls: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "Partial" },
  unpaid: { cls: "bg-red-500/20 text-red-400 border-red-500/30", label: "Unpaid" },
};

export default function OrderDetail({ orderId, onBack }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [holdReason, setHoldReason] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const designRef = useRef(null);
  const proofRef = useRef(null);

  const fetchOrder = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/oms/orders/${orderId}`, { withCredentials: true });
      setOrder(data);
      setTrackingNumber(data.tracking_number || "");
      setNotes(data.notes || "");
    } catch (err) {
      console.error("Failed:", err);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const updateStatus = async (status, reason) => {
    try {
      const body = { status };
      if (reason) body.hold_reason = reason;
      if (trackingNumber && status === "shipped") body.tracking_number = trackingNumber;
      await axios.patch(`${API}/oms/orders/${orderId}`, body, { withCredentials: true });
      fetchOrder();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed");
    }
  };

  const updateField = async (field, value) => {
    try {
      await axios.patch(`${API}/oms/orders/${orderId}`, { [field]: value }, { withCredentials: true });
      fetchOrder();
    } catch (err) {
      alert("Failed to update");
    }
  };

  const uploadFile = async (file, type) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/oms/orders/${orderId}/upload/${type}`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      fetchOrder();
    } catch (err) {
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const getDueColor = () => {
    if (!order?.due_date || order.status === "shipped") return "";
    const diff = (new Date(order.due_date) - new Date()) / (1000 * 60 * 60 * 24);
    if (diff < 0) return "text-red-400";
    if (diff < 2) return "text-yellow-400";
    return "text-zinc-400";
  };

  if (loading) return <div className="text-zinc-500 text-center py-12">Loading...</div>;
  if (!order) return <div className="text-zinc-500 text-center py-12">Order not found</div>;

  const currentIdx = STAGES.findIndex((s) => s.id === order.status);
  const isHeld = order.status === "on_hold" || order.status === "blocked";

  return (
    <div data-testid="order-detail" className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="text-zinc-400 hover:text-white" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h2 className="text-2xl font-bold text-white">{order.order_number}</h2>
        <Badge className={`${PAYMENT_MAP[order.payment_status]?.cls} border`}>
          {PAYMENT_MAP[order.payment_status]?.label}
        </Badge>
        {isHeld && (
          <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
            {order.status === "on_hold" ? "On Hold" : "Blocked"}
          </Badge>
        )}
      </div>

      {/* Progress bar */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="py-4">
          <div className="flex items-center overflow-x-auto gap-1">
            {STAGES.map((stage, idx) => {
              const done = idx <= currentIdx && !isHeld;
              const current = idx === currentIdx && !isHeld;
              return (
                <div key={stage.id} className="flex items-center flex-shrink-0">
                  <button
                    onClick={() => updateStatus(stage.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                      current ? "bg-orange-500 text-white" : done ? "bg-green-500/20 text-green-400" : "bg-zinc-800 text-zinc-500 hover:bg-zinc-700"
                    }`}
                    data-testid={`stage-btn-${stage.id}`}
                  >
                    {done && !current && <Check className="w-3 h-3" />}
                    {stage.label}
                  </button>
                  {idx < STAGES.length - 1 && <ChevronRight className="w-3 h-3 text-zinc-700 mx-1 flex-shrink-0" />}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left - Order details */}
        <div className="lg:col-span-2 space-y-4">
          {/* Customer Info */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2"><User className="w-4 h-4 text-orange-400" /> Customer</CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-zinc-500">Name</span>
                <p className="text-white font-medium">{order.customer_name}</p>
              </div>
              <div>
                <span className="text-zinc-500">Email</span>
                <p className="text-white">{order.customer_email}</p>
              </div>
              <div>
                <span className="text-zinc-500">Phone</span>
                <p className="text-white">{order.customer_phone || "N/A"}</p>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2"><Package className="w-4 h-4 text-orange-400" /> Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {order.items?.map((item, i) => (
                  <div key={i} className="bg-zinc-800 rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-white font-medium">{item.name}</span>
                      <span className="text-zinc-400 text-sm">x{item.quantity} @ ${item.unit_price?.toFixed(2)}</span>
                    </div>
                    {item.sizes && Object.keys(item.sizes).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1">
                        {Object.entries(item.sizes).map(([size, qty]) => (
                          <span key={size} className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded">{size}: {qty}</span>
                        ))}
                      </div>
                    )}
                    {item.colors?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1">
                        {item.colors.map((c) => (
                          <span key={c} className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded">{c}</span>
                        ))}
                      </div>
                    )}
                    {item.print_locations?.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-zinc-500">
                        <MapPin className="w-3 h-3" /> {item.print_locations.join(", ")}
                      </div>
                    )}
                    {item.notes && <p className="text-xs text-zinc-500 mt-1">{item.notes}</p>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Files */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2"><FileText className="w-4 h-4 text-orange-400" /> Files</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-zinc-400">Design Files</span>
                    <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300 text-xs" onClick={() => designRef.current?.click()} disabled={uploading} data-testid="upload-design-btn">
                      <Upload className="w-3 h-3 mr-1" /> Upload
                    </Button>
                    <input ref={designRef} type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "design")} />
                  </div>
                  {order.design_files?.length > 0 ? order.design_files.map((f) => (
                    <div key={f.id} className="text-xs text-zinc-400 bg-zinc-800 rounded px-2 py-1.5 mb-1 flex justify-between">
                      <span className="truncate">{f.original_filename}</span>
                      <span className="text-zinc-600 ml-2">{(f.size / 1024).toFixed(0)}KB</span>
                    </div>
                  )) : <p className="text-xs text-zinc-600">No design files yet</p>}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-zinc-400">Proof Files</span>
                    <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300 text-xs" onClick={() => proofRef.current?.click()} disabled={uploading} data-testid="upload-proof-btn">
                      <Upload className="w-3 h-3 mr-1" /> Upload
                    </Button>
                    <input ref={proofRef} type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "proof")} />
                  </div>
                  {order.proof_files?.length > 0 ? order.proof_files.map((f) => (
                    <div key={f.id} className="text-xs text-zinc-400 bg-zinc-800 rounded px-2 py-1.5 mb-1 flex justify-between">
                      <span className="truncate">{f.original_filename}</span>
                      <span className="text-zinc-600 ml-2">{(f.size / 1024).toFixed(0)}KB</span>
                    </div>
                  )) : <p className="text-xs text-zinc-600">No proof files yet</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status History */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base">Status History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {order.status_history?.slice().reverse().map((h, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 flex-shrink-0" />
                    <div>
                      <span className="text-white capitalize">{h.status?.replace(/_/g, " ")}</span>
                      <span className="text-zinc-600 ml-2">by {h.changed_by_name}</span>
                      <span className="text-zinc-600 ml-2">{new Date(h.timestamp).toLocaleString()}</span>
                      {h.reason && <p className="text-xs text-red-400 mt-0.5">Reason: {h.reason}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Production Job Ticket */}
          <Card className="bg-zinc-900 border-zinc-800" data-testid="job-ticket">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-orange-400" /> Production Job Ticket
                </CardTitle>
                <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300 text-xs h-7" onClick={() => window.print()} data-testid="print-job-ticket">
                  Print
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-zinc-800 rounded-lg p-4 space-y-3 text-sm print:bg-white print:text-black">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-zinc-500 text-xs">Order #</span>
                    <p className="text-white font-bold print:text-black">{order.order_number}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500 text-xs">Date</span>
                    <p className="text-white print:text-black">{new Date(order.created_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500 text-xs">Customer</span>
                    <p className="text-white print:text-black">{order.customer_name}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500 text-xs">Status</span>
                    <p className="text-orange-400 capitalize print:text-black">{order.status?.replace(/_/g, " ")}</p>
                  </div>
                </div>
                <div className="border-t border-zinc-700 pt-3">
                  <span className="text-zinc-500 text-xs block mb-2">Line Items</span>
                  {order.items?.map((item, i) => (
                    <div key={i} className="bg-zinc-900/50 rounded p-2 mb-2 print:bg-gray-100">
                      <div className="flex justify-between">
                        <span className="text-white font-medium print:text-black">{item.name}</span>
                        <span className="text-zinc-400 print:text-black">x{item.quantity}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-1 text-xs text-zinc-500 print:text-gray-600">
                        {item.garment_type && <span>Garment: {item.garment_type}</span>}
                        {item.print_locations?.length > 0 && <span>Print: {item.print_locations.join(", ")}</span>}
                        {item.colors?.length > 0 && <span>Colors: {item.colors.join(", ")}</span>}
                      </div>
                      {item.sizes && Object.keys(item.sizes).length > 0 && (
                        <div className="mt-1 text-xs">
                          <span className="text-zinc-500 print:text-gray-600">Sizes: </span>
                          {Object.entries(item.sizes).map(([size, qty]) => (
                            <span key={size} className="text-zinc-300 mr-2 print:text-black">{size}({qty})</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {order.notes && (
                  <div className="border-t border-zinc-700 pt-3">
                    <span className="text-zinc-500 text-xs">Special Instructions</span>
                    <p className="text-amber-400 font-medium mt-1 print:text-black">{order.notes}</p>
                  </div>
                )}
                <div className="border-t border-zinc-700 pt-3 grid grid-cols-3 gap-3 text-xs text-zinc-500 print:text-gray-600">
                  <div>
                    <span className="block">Total Amount</span>
                    <span className="text-white font-bold text-base print:text-black">${order.total_amount?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="block">Payment</span>
                    <span className={`font-medium ${order.payment_status === 'paid' ? 'text-green-400' : 'text-red-400'} print:text-black`}>
                      {order.payment_status?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <span className="block">Workflow</span>
                    <span className="text-white capitalize print:text-black">{order.workflow_type}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          {/* Total */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-4">
              <div className="text-center">
                <DollarSign className="w-6 h-6 text-green-400 mx-auto mb-1" />
                <div className="text-3xl font-bold text-white">${order.total_amount?.toLocaleString()}</div>
                <select
                  value={order.payment_status}
                  onChange={(e) => updateField("payment_status", e.target.value)}
                  className="mt-2 bg-zinc-800 border border-zinc-700 text-zinc-300 rounded px-3 py-1 text-sm w-full"
                  data-testid="payment-status-select"
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Due date */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-4">
              <Label className="text-zinc-400 text-xs">Due Date</Label>
              <Input
                type="date"
                value={order.due_date ? order.due_date.slice(0, 10) : ""}
                onChange={(e) => updateField("due_date", e.target.value ? new Date(e.target.value).toISOString() : null)}
                className={`mt-1 bg-zinc-800 border-zinc-700 ${getDueColor()}`}
                data-testid="due-date-input"
              />
              {order.due_date && (
                <p className={`text-xs mt-1 ${getDueColor()}`}>
                  {(() => {
                    const diff = Math.ceil((new Date(order.due_date) - new Date()) / (1000 * 60 * 60 * 24));
                    if (diff < 0) return `${Math.abs(diff)} days overdue`;
                    if (diff === 0) return "Due today";
                    return `${diff} days remaining`;
                  })()}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Tracking */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-4">
              <Label className="text-zinc-400 text-xs">Tracking Number</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="Enter tracking #"
                  className="bg-zinc-800 border-zinc-700 text-white text-sm"
                  data-testid="tracking-input"
                />
                <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => updateField("tracking_number", trackingNumber)}>
                  <Truck className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Hold/Block */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-4 space-y-2">
              <Label className="text-zinc-400 text-xs">Put On Hold / Block</Label>
              <Textarea
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                placeholder="Reason (required)..."
                className="bg-zinc-800 border-zinc-700 text-white text-sm"
                rows={2}
                data-testid="hold-reason-input"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                  onClick={() => { if (holdReason) updateStatus("on_hold", holdReason); else alert("Reason required"); }}
                  data-testid="on-hold-btn"
                >
                  On Hold
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                  onClick={() => { if (holdReason) updateStatus("blocked", holdReason); else alert("Reason required"); }}
                  data-testid="blocked-btn"
                >
                  Blocked
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-4">
              <Label className="text-zinc-400 text-xs">Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
                rows={3}
                data-testid="order-notes"
              />
              <Button
                size="sm"
                variant="outline"
                className="mt-2 border-zinc-700 text-zinc-300 w-full"
                onClick={() => updateField("notes", notes)}
              >
                Save Notes
              </Button>
            </CardContent>
          </Card>

          <div className="text-xs text-zinc-600 space-y-1">
            <p>Created: {new Date(order.created_at).toLocaleString()}</p>
            <p>By: {order.created_by_name}</p>
            <p>Source: {order.source}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
